import type { ExperimentName } from './registry';
import { parseCookieHeader } from '@/src/lib/cookies/cookieParser';

const COOKIE_PREFIX = 'xp_';

function parseCookies(cookieString: string): Record<string, string> {
  return parseCookieHeader(cookieString, { decodeValues: true });
}

function cookieKey(name: ExperimentName): string {
  return `${COOKIE_PREFIX}${name}`;
}

export function getOverride(name: ExperimentName, cookieString?: string): string | undefined {
  const raw = cookieString ?? (typeof document !== 'undefined' ? document.cookie : '');
  const value = parseCookies(raw)[cookieKey(name)];
  return value && value.length > 0 ? value : undefined;
}

/**
 * Returns every `xp_`-prefixed override cookie keyed by raw experiment name.
 * The type is intentionally `Record<string, string>` rather than narrowed to
 * `ExperimentName` because the cookies on the wire don't honor the registry
 * — callers narrow per-name via `isExperimentName` or by typed indexing.
 */
export function getAllOverrides(cookieString?: string): Record<string, string> {
  const raw = cookieString ?? (typeof document !== 'undefined' ? document.cookie : '');
  const cookies = parseCookies(raw);
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(cookies)) {
    if (!key.startsWith(COOKIE_PREFIX)) continue;
    const experimentName = key.slice(COOKIE_PREFIX.length);
    if (value && value.length > 0) {
      result[experimentName] = value;
    }
  }
  return result;
}

export function setOverride(name: ExperimentName, value: string): void {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(value);
  document.cookie = `${cookieKey(name)}=${encoded}; path=/; max-age=31536000; SameSite=Lax`;
}

export function clearOverride(name: ExperimentName): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${cookieKey(name)}=; path=/; max-age=0; SameSite=Lax`;
}

export function clearAllOverrides(cookieString?: string): void {
  if (typeof document === 'undefined') return;
  const raw = cookieString ?? document.cookie;
  const cookies = parseCookies(raw);
  for (const key of Object.keys(cookies)) {
    if (!key.startsWith(COOKIE_PREFIX)) continue;
    document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax`;
  }
}
